import { useRef } from "react";
import { DateRangeFilter } from "./internal/types";
import {
  MaskedDateRangeInput,
  MaskedDateRangeInputRef,
} from "../../../../components/DateFieldRange/internal/MaskedDateRangeInput";
import { DateFieldRangeChange } from "../../../../components/DateFieldRange";
import { useDateFieldRangeConversion } from "../../../../components/DateFieldRange/internal/useDateFieldRangeConversion";
import { useDateFieldRangeState } from "../../../../components/DateFieldRange/internal/useDateFieldRangeState";
import Flex from "../../../../components/Flex";
import { DateFieldRangeCalendar } from "../../../../components/DateFieldRange/internal/DateFieldRangeCalendar";

/**
 * Props for the FilterDateRange component
 */
interface FilterDateRangeProps {
  /**
   * The date range filter configuration object containing mode and other settings
   */
  filter: DateRangeFilter;
  /**
   * The current draft value for the date range filter with start and end dates in ISO 8601 format
   */
  draftValue: { startDate: string | null; endDate: string | null } | null;
  /**
   * Callback function to update the draft value when the date range changes
   */
  onDraftChange: (
    value: { startDate: string | null; endDate: string | null } | null,
  ) => void;
}

/**
 * FilterDateRange component for selecting a date range in toolbar filters.
 *
 * Features:
 * - Renders a masked date range input field with format validation
 * - Provides calendar popup for date range selection
 * - Integrates with toolbar filter state management
 * - Supports controlled and uncontrolled date range selection
 * - Maintains draft state for filter changes
 * - Handles start and end date selection independently
 */
export const FilterDateRange = ({
  filter,
  draftValue,
  onDraftChange,
}: FilterDateRangeProps) => {
  const maskedDateRangeInputRef = useRef<MaskedDateRangeInputRef>(null);

  const handleChange = (change: DateFieldRangeChange) => {
    // Only call onDraftChange when we have a complete date range (both start and end)
    // or when clearing the range (both null)
    const hasCompleteRange = change.startDate && change.endDate;
    const isClearingRange = !change.startDate && !change.endDate;

    if (hasCompleteRange || isClearingRange) {
      onDraftChange({
        startDate: change.startDate,
        endDate: change.endDate,
      });
    }
  };

  const { value, onChange } = useDateFieldRangeConversion({
    value: draftValue,
    onChange: handleChange,
  });

  const { handleInputChange, handleCalendarSelection } = useDateFieldRangeState(
    {
      valueProp: value,
      onChange: (change) => {
        // Only call handleChange if input is valid and we have a complete range, or if clearing
        const hasCompleteRange = change.startDate && change.endDate;
        const isClearingRange = change.isInputEmpty;

        if ((change.isInputValid && hasCompleteRange) || isClearingRange) {
          onChange({ ...change, isDateRangeValid: true });
        }
      },
    },
  );

  return (
    <Flex gap={4} direction="column">
      <MaskedDateRangeInput
        mode={filter.mode}
        ref={maskedDateRangeInputRef}
        onChange={handleInputChange}
        startDate={value?.startDate ?? null}
        endDate={value?.endDate ?? null}
        autoComplete="off"
      />

      <DateFieldRangeCalendar
        startDate={value?.startDate ?? null}
        endDate={value?.endDate ?? null}
        onSelection={handleCalendarSelection}
      />
    </Flex>
  );
};
